<template>
  <div
    class="flex text-sm"
    :style="styles"
    :class="[
      textClasses,
      checkboxContainerClasses,
      [disabled ? 'opacity-50 line-through' : ''],
      [itemsCenter ? 'items-center' : 'items-start'],
    ]"
  >
    <div class="relative flex items-center justify-center">
      <input
        ref="checkbox"
        type="checkbox"
        :id="id"
        :name="name"
        @change="emitChecked"
        @keydown.enter="emitChecked"
        :checked="props.modelValue"
        :readonly="props.readonly"
        tabindex="0"
        class="cursor-pointer appearance-none border transition-all duration-100 focus:outline-none focus:ring-2 ring-color ring-offset-2"
        :class="[[checkboxClasses],[checkboxSize],[mxPx ? 'mx-px px-px' : '']]"
      />
      <CheckIcon
        v-if="props.modelValue"
        @click="emitChecked"
        class="absolute cursor-pointer text-white transition-all duration-100 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mb-px w-4 h-4"
      />
    </div>
    <button type="button"
            :id="name"
            @click="emitChecked"
            class="cursor-pointer block text-left max-w-fit"
            :class="[[!itemsCenter && '-mt-0.5'],[textClass]]">
      <slot/>
    </button>
  </div>
</template>

<script setup>
import {CheckIcon} from "@heroicons/vue/24/outline";
import {computed, ref} from "vue";
import {addOpacityToHex} from "../helpers/Color";

const props = defineProps({
  id: String,
  modelValue: Boolean,
  disabled: Boolean,
  itemsCenter: Boolean,
  useValue: Boolean,
  customColors: Boolean,
  customAccentHex: String,
  name: String,
  checkboxSize: {
    type: String,
    default: "h-4 w-4 rounded-[5px]",
  },
  checkboxContainerClasses: {
    type: String,
    default: "gap-1.5",
  },
  textClass: String,
  mxPx: {
    type: Boolean,
    default: true,
  },
  readonly: Boolean,
});

const emits = defineEmits(["update:modelValue"]);

const checkbox = ref(null);

defineExpose({checkbox});

const textClasses = computed(() => {
  if (props.customColors) return "";
  return "text-slate-500";
});

const checkboxClasses = computed(() => {
  if (props.customColors) return "";
  if (props.modelValue) return "is-selected";
  else return "border-slate-300 not-selected";
});

const emitChecked = () => emits("update:modelValue", !props.modelValue);

const styles = computed(() => {
  return {
    "accent-color": props.customAccentHex || "#2c5db6",
    "--accent-color": props.customAccentHex || "#2c5db6",
    "border-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.5),
    "--ring-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.25),
    "--hover-background-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.05),
  };
});
</script>

<style scoped>
.is-selected {
  background-color: var(--accent-color);
  border-color: var(--accent-color);
}

.not-selected:focus, .not-selected:hover {
  border-color: var(--accent-color);
  background-color: var(--hover-background-color);
}

.ring-color {
  --tw-ring-color: var(--ring-color);
}

</style>
